import { resolveCurrencyImage } from "@/helpers/currencyImage"
import {
  compareCurrencyCodes,
  presentationOf
} from "@/helpers/currencyPresentation"
import {
  buildRollingCharacters,
  buildScaleIndex,
  compareAmounts,
  counterWidthTexts,
  formatCounterAmount,
  ICompactSuffixes,
  parseAmount
} from "@/helpers/money"
import {
  ICounterInput,
  ICounterRecord,
  IWalletCounterView
} from "@/hooks/useWalletCounters/types"
import { ICurrencyEntry } from "@/store/apis/catalogsAPI/catalogs.types"
import { IWalletBalance } from "@/store/apis/walletAPI/wallet.types"

const PLACEHOLDER_AMOUNT = "0"

export const deriveCounters = (
  balances: readonly IWalletBalance[] | null,
  currencies: readonly ICurrencyEntry[],
  locale: string,
  suffixes: ICompactSuffixes,
  order: readonly string[]
): ICounterInput[] => {
  const scales = buildScaleIndex(currencies)
  const rows =
    balances ??
    currencies.map((entry) => ({
      currencyCode: entry.code,
      amount: PLACEHOLDER_AMOUNT
    }))
  return rows
    .flatMap((row): ICounterInput[] => {
      const scale = scales.get(row.currencyCode)
      const amount = parseAmount(row.amount)
      const formatted =
        scale === undefined
          ? null
          : formatCounterAmount(row.amount, scale, locale, suffixes)
      return scale === undefined || amount === null || formatted === null
        ? []
        : [
            {
              currencyCode: row.currencyCode,
              amount,
              text: formatted.text,
              exactText: formatted.exactText,
              widthTexts: counterWidthTexts(scale, locale, suffixes)
            }
          ]
    })
    .sort((a, b) => compareCurrencyCodes(a.currencyCode, b.currencyCode, order))
}

export const trackCounter = (
  record: ICounterRecord | undefined,
  input: ICounterInput
): ICounterRecord => {
  const full = input.exactText === null
  if (record === undefined) {
    return {
      amount: input.amount,
      full,
      text: input.text,
      previousText: null,
      direction: "none",
      changeId: 0
    }
  }
  if (record.amount === input.amount) {
    if (record.text === input.text && record.full === full) {
      return record
    }
    return {
      ...record,
      full,
      text: input.text,
      previousText: null,
      direction: "none"
    }
  }
  return {
    amount: input.amount,
    full,
    text: input.text,
    previousText: record.full ? record.text : null,
    direction: compareAmounts(input.amount, record.amount) > 0 ? "up" : "down",
    changeId: record.changeId + 1
  }
}

export const trackCounters = (
  records: ReadonlyMap<string, ICounterRecord>,
  inputs: readonly ICounterInput[],
  loaded: boolean
): ReadonlyMap<string, ICounterRecord> =>
  loaded
    ? new Map(
        inputs.map((input) => [
          input.currencyCode,
          trackCounter(records.get(input.currencyCode), input)
        ])
      )
    : records

export const toCounterViews = (
  inputs: readonly ICounterInput[],
  records: ReadonlyMap<string, ICounterRecord>,
  openCode: string | null
): IWalletCounterView[] =>
  inputs.map((input) => {
    const record = records.get(input.currencyCode)
    const presentation = presentationOf(input.currencyCode)
    return {
      currencyCode: input.currencyCode,
      amountText: input.text,
      exactText: input.exactText,
      widthTexts: input.widthTexts,
      rollingCharacters: buildRollingCharacters(
        record?.full ? record.previousText : null,
        input.text
      ),
      direction: record?.direction ?? "none",
      changeId: record?.changeId ?? 0,
      iconUrl: resolveCurrencyImage(input.currencyCode),
      tone: presentation.tone,
      hasAddButton: presentation.hasAddButton,
      isHintOpen: openCode === input.currencyCode && input.exactText !== null
    }
  })

export const compactSuffixesOf = (
  translate: (key: string) => string
): ICompactSuffixes => ({
  thousand: translate("compactSuffix.thousand"),
  million: translate("compactSuffix.million"),
  billion: translate("compactSuffix.billion"),
  trillion: translate("compactSuffix.trillion"),
  quadrillion: translate("compactSuffix.quadrillion"),
  quintillion: translate("compactSuffix.quintillion")
})
